import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Calculator,
  TrendingUp,
  MapPin,
  BarChart3,
  Building2,
  DollarSign,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { generateSalaryPageUrl, extractLocationContext, createOccupationSlug } from '@/lib/occupationUtils';
import { cn } from '@/lib/utils';
import SocCodeBadge from './SocCodeBadge';

interface RelatedMetricsProps {
  occupation?: {
    title?: string;
    occ_title?: string;
    soc?: string;
    soc_code?: string;
    slug?: string;
  };
  location?: string;
  currentMetric?: 'salary' | 'trends' | 'best-states' | 'rankings' | 'calculator' | 'industry';
  showDescriptions?: boolean;
  className?: string;
  variant?: 'card' | 'inline' | 'compact';
}

const RelatedMetrics: React.FC<RelatedMetricsProps> = ({
  occupation,
  location,
  currentMetric,
  showDescriptions = true,
  className,
  variant = 'card',
}) => {
  const navigate = useNavigate();
  const currentLocation = useLocation();
  
  // Determine target location
  const targetLocation = location || extractLocationContext(currentLocation.pathname) || 'united-states';
  
  // Get occupation data
  const occupationTitle = occupation?.title || occupation?.occ_title || '';
  const socCode = occupation?.soc || occupation?.soc_code || '';
  const occupationSlug = occupation?.slug || (occupationTitle ? createOccupationSlug(occupationTitle) : '');
  
  // Generate URLs for different metrics
  const salaryUrl = occupationSlug && occupationTitle
    ? generateSalaryPageUrl({ ...occupation, soc: socCode, soc_code: socCode }, targetLocation)
    : '/';
  
  const trendsUrl = occupationSlug && occupationTitle
    ? `/trends/${occupationSlug}/${targetLocation}/${socCode ? `?soc=${socCode}` : ''}`
    : '/trends/';
  
  const bestStatesUrl = occupationSlug
    ? `/best-states/${occupationSlug}/${socCode ? `?soc=${socCode}` : ''}`
    : '/best-states/';
  
  const rankingsUrl = socCode
    ? `/rankings?soc=${socCode.substring(0, 2)}-`
    : '/rankings';
  
  const calculatorUrl = occupationSlug && occupationTitle
    ? `/calculators/salary/?occupation=${encodeURIComponent(occupationTitle)}&soc=${socCode}`
    : '/calculators/salary/';
  
  const industryUrl = occupationSlug
    ? `/industry/?occupation=${occupationSlug}${socCode ? `&soc=${socCode}` : ''}`
    : '/industry/';
  
  const metrics = [
    {
      id: 'salary',
      label: 'Salary Details',
      shortLabel: 'Salary',
      description: 'View comprehensive salary data including percentiles, industry breakdown, and career insights',
      icon: DollarSign,
      url: salaryUrl,
      available: !!occupationSlug && !!occupationTitle,
      color: 'text-blue-600',
    },
    {
      id: 'trends',
      label: 'Salary Trends',
      shortLabel: 'Trends',
      description: 'Historical wage analysis with growth projections and economic context',
      icon: TrendingUp,
      url: trendsUrl,
      available: !!occupationSlug && !!occupationTitle,
      color: 'text-green-600',
    },
    {
      id: 'best-states',
      label: 'Best States',
      shortLabel: 'States',
      description: 'Compare salaries across all 50 states with cost-of-living adjustments',
      icon: MapPin,
      url: bestStatesUrl,
      available: !!occupationSlug,
      color: 'text-purple-600',
    },
    {
      id: 'rankings',
      label: 'Occupation Rankings',
      shortLabel: 'Rankings',
      description: 'See how this occupation ranks among all jobs by salary and employment',
      icon: BarChart3,
      url: rankingsUrl,
      available: true,
      color: 'text-orange-600',
    },
    {
      id: 'calculator',
      label: 'Salary Calculator',
      shortLabel: 'Calculator',
      description: 'Convert hourly to annual wages and find comparable jobs at similar pay levels',
      icon: Calculator,
      url: calculatorUrl,
      available: true,
      color: 'text-indigo-600',
    },
    {
      id: 'industry',
      label: 'Industry Data',
      shortLabel: 'Industry',
      description: 'View industry breakdown and employment distribution',
      icon: Building2,
      url: industryUrl,
      available: !!occupationSlug,
      color: 'text-teal-600',
    },
  ];
  
  const availableMetrics = metrics.filter(m => m.available);
  
  if (variant === 'inline') {
    return (
      <div className={cn('flex flex-wrap items-center gap-2', className)}>
        <span className="text-sm text-muted-foreground">Related:</span>
        {availableMetrics
          .filter(m => m.id !== currentMetric)
          .map((metric) => {
            const Icon = metric.icon;
            return (
              <Button
                key={metric.id}
                variant="outline"
                size="sm"
                className="h-8"
                onClick={() => navigate(metric.url)}
              >
                <Icon className="mr-1.5 h-3.5 w-3.5" />
                {metric.shortLabel}
              </Button>
            );
          })}
      </div>
    );
  }
  
  if (variant === 'compact') {
    return (
      <div className={cn('space-y-2', className)}>
        {availableMetrics
          .filter(m => m.id !== currentMetric)
          .map((metric) => {
            const Icon = metric.icon;
            return (
              <Button
                key={metric.id}
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate(metric.url)}
              >
                <Icon className="mr-2 h-4 w-4" />
                {metric.label}
                <ArrowRight className="ml-auto h-4 w-4" />
              </Button>
            );
          })}
      </div>
    );
  }
  
  // Card variant (default)
  return (
    <Card className={cn('w-full', className)}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">
            Related Metrics
            {occupationTitle && socCode && (
              <span className="text-base font-normal text-muted-foreground ml-2">
                for {occupationTitle}
              </span>
            )}
          </CardTitle>
          {socCode && (
            <SocCodeBadge
              socCode={socCode}
              occupation={occupation}
              location={location}
              size="sm"
            />
          )}
        </div>
        {occupationTitle && socCode && (
          <p className="text-sm text-muted-foreground mt-1">
            Explore all available data and insights for this occupation
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {availableMetrics
            .filter(m => m.id !== currentMetric)
            .map((metric) => {
              const Icon = metric.icon;
              const isActive = currentMetric === metric.id;
              
              return (
                <div
                  key={metric.id}
                  className={cn(
                    'p-4 rounded-lg border transition-all',
                    'hover:border-primary hover:shadow-sm',
                    isActive && 'border-primary bg-primary/5'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      'p-2 rounded-lg bg-muted',
                      metric.color.replace('text-', 'bg-').replace('-600', '-100')
                    )}>
                      <Icon className={cn('h-5 w-5', metric.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-sm">{metric.label}</h4>
                        {isActive && (
                          <Badge variant="secondary" className="text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Current
                          </Badge>
                        )}
                      </div>
                      {showDescriptions && (
                        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                          {metric.description}
                        </p>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => navigate(metric.url)}
                      >
                        View {metric.shortLabel}
                        <ArrowRight className="ml-1 h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
        
        {availableMetrics.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">No related metrics available.</p>
            <p className="text-xs mt-1">Select an occupation to see related data.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RelatedMetrics;
